import { Box, Group, Text, Badge, Tooltip, Loader } from '@mantine/core';
import { IconInfoCircle, IconRefresh } from '@tabler/icons-react';
import { usePortalVersion, getCurrentEnvironment, formatVersionDisplay } from '../hooks/usePortalVersion';

/**
 * Portal Version Display Component
 * Shows current version, environment, and deployment info
 */
export function PortalVersion({ compact = false }) {
  const environment = getCurrentEnvironment();
  const { 
    version, 
    loading, 
    error, 
    deployedAt, 
    deployedBy, 
    commit, 
    branch,
    isRollback 
  } = usePortalVersion(environment);
  
  const { version: displayVersion, environment: envLabel, color } = formatVersionDisplay(version, environment);

  if (loading) {
    return (
      <Box px="md" py="xs">
        <Group gap="xs" justify="center">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">Loading version...</Text>
        </Group>
      </Box>
    );
  }

  if (error) {
    return (
      <Box px="md" py="xs">
        <Text size="xs" c="dimmed">Version unavailable</Text>
      </Box>
    );
  }

  if (compact) {
    // Compact version for collapsed sidebar - very subtle
    return (
      <Tooltip
        label={
          <Box>
            <Text size="xs">{displayVersion} • {envLabel}</Text>
            {deployedAt && (
              <Text size="xs" c="dimmed">
                {new Date(deployedAt).toLocaleDateString()}
              </Text>
            )}
          </Box>
        }
        position="right"
        withArrow
      >
        <Box 
          px="md" 
          py="xs" 
          style={{ 
            opacity: 0.4,
            cursor: 'help',
            textAlign: 'center'
          }}
        >
          <Text size="xs" c="dimmed">{displayVersion}</Text>
        </Box>
      </Tooltip>
    );
  }

  // Full version for expanded sidebar - much more subtle
  return (
    <Tooltip
      label={
        <Box>
          <Text size="xs">Version {displayVersion}</Text>
          <Text size="xs" c="dimmed">Environment: {envLabel}</Text>
          {deployedAt && (
            <Text size="xs" c="dimmed">
              Deployed: {formatDeploymentTime(deployedAt)}
            </Text>
          )}
          {deployedBy?.name && (
            <Text size="xs" c="dimmed">By: {deployedBy.name}</Text>
          )}
          {commit && commit !== 'unknown' && (
            <Text size="xs" c="dimmed">Commit: {commit.substring(0, 7)}</Text>
          )}
        </Box>
      }
      position="top"
      withArrow
      openDelay={500}
    >
      <Box 
        px="md" 
        py={4}
        style={{ 
          opacity: 0.35,
          cursor: 'help',
          transition: 'opacity 0.2s ease',
          ':hover': {
            opacity: 0.5
          }
        }}
      >
        <Text size="xs" c="dimmed" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
          {displayVersion} • {envLabel.toLowerCase()}
        </Text>
      </Box>
    </Tooltip>
  );
}

/**
 * Format deployment time as relative time
 */
function formatDeploymentTime(date) {
  if (!date) return 'Unknown deployment time';
  
  const now = new Date();
  const deployDate = new Date(date);
  const diffMs = now - deployDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Just deployed';
  } else if (diffMins < 60) {
    return `Deployed ${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `Deployed ${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `Deployed ${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    return `Deployed on ${deployDate.toLocaleDateString()}`;
  }
}

export default PortalVersion;